import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { Destination } from '../context/AxiosContext'

const RETRYABLE_STATUSES = new Set([429, 503, 504])
const MAX_RETRIES = 5
const BASE_DELAY_MS = 500
const MAX_DELAY_MS = 30_000

// Opt-in via the `X-Patient-Retry: true` request header. Used for background tree-load Graph
// calls (folder children listings) where the user is better served by a slow eventual result
// than by a fast visible failure — see HAM-4146.
export const PATIENT_RETRY_HEADER = 'X-Patient-Retry'
const PATIENT_MAX_RETRIES = 10
const PATIENT_MAX_DELAY_MS = 120_000

type RetryConfig = { __graphRetryCount?: number }

export const parseRetryAfter = (header: string | number | undefined): number | undefined => {
    if (header === undefined || header === null || header === '') {
        return undefined
    }
    const asNumber = typeof header === 'number' ? header : Number(header)
    if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
        return Math.max(0, asNumber * 1000)
    }
    const date = Date.parse(String(header))
    if (!Number.isNaN(date)) {
        return Math.max(0, date - Date.now())
    }
    return undefined
}

const backoffDelay = (attempt: number, maxDelayMs: number): number => Math.min(maxDelayMs, BASE_DELAY_MS * 2 ** attempt)

const isGraphDestination = (error: AxiosError): boolean => {
    const destination = error.config?.headers?.['Destination']
    return destination === Destination.Documents || destination === Destination.SharePoint
}

const isPatientRetry = (error: AxiosError): boolean => {
    const header = error.config?.headers?.[PATIENT_RETRY_HEADER]
    return header === 'true' || header === true
}

export const createGraphRetryHandler = (axios: AxiosInstance) => {
    return (error: AxiosError): Promise<AxiosResponse> => {
        const status = error.response?.status
        if (!status || !RETRYABLE_STATUSES.has(status) || !isGraphDestination(error) || !error.config) {
            return Promise.reject(error)
        }

        const patient = isPatientRetry(error)
        const maxRetries = patient ? PATIENT_MAX_RETRIES : MAX_RETRIES
        const maxDelayMs = patient ? PATIENT_MAX_DELAY_MS : MAX_DELAY_MS

        const config = error.config as typeof error.config & RetryConfig
        const attempt = config.__graphRetryCount ?? 0
        if (attempt >= maxRetries) {
            return Promise.reject(error)
        }

        const retryAfter = parseRetryAfter(error.response?.headers?.['retry-after'])
        const delay = retryAfter ?? backoffDelay(attempt, maxDelayMs)

        config.__graphRetryCount = attempt + 1

        return new Promise<AxiosResponse>((resolve, reject) => {
            setTimeout(() => {
                axios.request(config).then(resolve).catch(reject)
            }, delay)
        })
    }
}
